import { CAMPAIGN_FILES_API_PAGE_SIZE, CAMPAIGN_FILES_MVP_LIMIT, type CampaignFile } from '@/domain/campaign.types'
import type {
	CampaignFileAssociationPort,
	CampaignFileDiscoveryCandidate,
	CampaignFileDiscoveryPort,
	CampaignFilesPort,
} from '@/domain/campaign-ports'
import { campaignService } from '@/services/campaign.service'
import { fileService } from '@/services/file.service'
import { mapCampaignHttpError } from './campaign-http-error'

// HTTP-backed CampaignFilesPort + CampaignFileDiscoveryPort + CampaignFileAssociationPort.
// The preserved in-memory campaign-files-adapter.ts is untouched; this is a new,
// independent module — mirrors campaign-crud-http-adapter.ts bundling two ports
// into one file.

/**
 * Requests up to two backend pages of `CAMPAIGN_FILES_API_PAGE_SIZE`, entirely
 * internal to this adapter — no page number, "can load more," or "loading
 * more" state crosses either port's boundary. A second page is requested only
 * when the first returned exactly a full page; a third page is never
 * requested. Results are concatenated in backend order (no client-side sort),
 * deduplicated by `fileId`, and truncated to `CAMPAIGN_FILES_MVP_LIMIT`.
 */
async function accumulateUpToTwoPages<T>(
	fetchPage: (page: number) => Promise<readonly T[]>,
	idOf: (item: T) => string,
): Promise<T[]> {
	const firstPage = await fetchPage(1)
	const pages = firstPage.length === CAMPAIGN_FILES_API_PAGE_SIZE ? [...firstPage, ...(await fetchPage(2))] : firstPage

	const seen = new Set<string>()
	const deduped: T[] = []
	for (const item of pages) {
		const id = idOf(item)
		if (seen.has(id)) continue
		seen.add(id)
		deduped.push(item)
	}
	return deduped.slice(0, CAMPAIGN_FILES_MVP_LIMIT)
}

export const campaignFilesHttpAdapter: CampaignFilesPort = {
	async listAssociatedFiles(campaignId: string): Promise<CampaignFile[]> {
		try {
			return await accumulateUpToTwoPages(
				(page) => campaignService.listAssociatedFiles(campaignId, page, CAMPAIGN_FILES_API_PAGE_SIZE),
				(file) => file.fileId,
			)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
}

export const campaignFileDiscoveryHttpAdapter: CampaignFileDiscoveryPort = {
	async discoverFiles(): Promise<readonly CampaignFileDiscoveryCandidate[]> {
		try {
			const files = await accumulateUpToTwoPages(
				(page) => fileService.discover({}, {}, CAMPAIGN_FILES_API_PAGE_SIZE, page),
				(file) => file.fileId,
			)
			return files.map((file) => ({ file, eligible: true }))
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
}

export const campaignFileAssociationHttpAdapter: CampaignFileAssociationPort = {
	async associateFiles(campaignId: string, fileIds: readonly string[]): Promise<void> {
		try {
			await campaignService.associateFiles(campaignId, fileIds)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
}
